'use client';

import React, { useState } from 'react';
import { Button } from '@ui/components/ui/button';
import { Card, CardContent } from '@ui/components/ui/card';
import { useLocale, useTranslations } from 'next-intl';
import { Locale } from '@ui/lib/locale';
import Logo from '@ui/components/ui/logo';
import LanguageSelector from '@ui/components/ui/language-selector';
import { setLocale } from '@/i18n/request';
import { toast } from '@ui/components/ui/sonner';
import ChangeSettingsReminder from '@/components/onboarding/change-settings-reminder';
import { useUser } from '@/contexts/user-context';

interface WelcomeStepProps {
  onComplete: (data: { selectedLocale?: string }) => void;
  isLoading: boolean;
}

export function WelcomeStep({ onComplete, isLoading }: WelcomeStepProps) {
  const t = useTranslations('Onboarding.welcome');
  const tNav = useTranslations('Onboarding.navigation');
  const tCommon = useTranslations('Common');
  const { user, updatePreferences } = useUser();
  const [selectedLocale, setSelectedLocale] = useState<Locale>(useLocale() as Locale);

  const handleLanguageChange = async (locale: Locale) => {
    setSelectedLocale(locale);
    setLocale(locale);
  };

  const handleNext = async () => {
    try {
      await updatePreferences({ locale: selectedLocale });
    } catch (e) {
      console.error('Error updating language in user onboarding', user.id, e);
      toast.error(t('languageUpdateError'));
    }

    onComplete({ selectedLocale });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="text-center space-y-4">
        <div className="text-3xl flex flex-col md:flex-row gap-2 justify-center items-center">
          <h2 className="font-bold">{t('title')}</h2>
          <Logo />
        </div>
        <p className="text-lg text-muted-foreground">{t('subtitle')}</p>
        <Card className="bg-secondary/40 border-secondary max-w-lg mx-auto">
          <CardContent>
            <p className="text-secondary-foreground font-medium">{t('creditsDisclaimer')}</p>
          </CardContent>
        </Card>
      </div>

      <LanguageSelector value={selectedLocale} onValueChange={handleLanguageChange} className="self-center" variant="full" />

      <div className="max-w-lg mx-auto">
        <ChangeSettingsReminder className="mt-2 text-center" />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleNext} disabled={isLoading} size="lg">
          {isLoading ? tCommon('states.loading') : tNav('next')}
        </Button>
      </div>
    </div>
  );
}
